import React from 'react';
import { ShoppingCart, Stethoscope, Building2, GraduationCap, Plane, Factory, Film, DollarSign, ArrowUpRight } from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: ShoppingCart,
      name: 'E-commerce',
      subtitle: 'Storefronts, POS & Marketplaces',
      badge: 'DIGITAL PRODUCT',
      color: 'from-cyan-500 to-blue-600',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      link: '/ecommerce-solutions'
    },
    {
      icon: Stethoscope,
      name: 'Healthcare',
      subtitle: 'Telemedicine, EHR & NCD Portals',
      badge: 'CLINICAL ERP',
      color: 'from-blue-500 to-purple-600',
      image: '/images/ncd_healthcare_dashboard.png',
      link: '/hospital-management'
    },
    {
      icon: Building2,
      name: 'Real Estate',
      subtitle: 'Property MLS & Tenant Portals',
      badge: 'MLS PLATFORM',
      color: 'from-purple-500 to-pink-600',
      image: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=800&q=80',
      link: '/enterprise-industry'
    },
    {
      icon: GraduationCap,
      name: 'Education',
      subtitle: 'SCORM LMS & EdTech Portals',
      badge: 'LMS DASHBOARD',
      color: 'from-pink-500 to-red-600',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
      link: '/lms-solution'
    },
    {
      icon: Plane,
      name: 'Travel & Hospitality',
      subtitle: 'Booking Engines & Travel CRM',
      badge: 'BOOKING ENGINE',
      color: 'from-red-500 to-orange-600',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      link: '/enterprise-industry'
    },
    {
      icon: Factory,
      name: 'Manufacturing',
      subtitle: 'Supply Chain & IoT ERP Dashboards',
      badge: 'ENTERPRISE ERP',
      color: 'from-orange-500 to-yellow-600',
      image: 'https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=800&q=80',
      link: '/erp-solution'
    },
    {
      icon: Film,
      name: 'Media & Entertainment',
      subtitle: 'OTT Streaming & Content CMS',
      badge: 'MEDIA CMS',
      color: 'from-yellow-500 to-green-600',
      image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&q=80',
      link: '/cms-solution'
    },
    {
      icon: DollarSign,
      name: 'Finance & Insurance',
      subtitle: 'Fintech Dashboards & Banking APIs',
      badge: 'FINTECH APP',
      color: 'from-green-500 to-cyan-600',
      image: 'https://images.unsplash.com/photo-1642543492481-44e81e3914a7?w=800&q=80',
      link: '/enterprise-industry'
    }
  ];

  return (
    <section className="py-24 bg-gray-950 relative overflow-hidden text-left">
      <div className="container mx-auto px-6 relative z-10">

        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
              Industries We Serve
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            Customized Digital Solutions for <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Every Industry</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed">
            We engineer industry-specific web dashboards, SaaS platforms, and mobile apps tailored to meet unique regulatory standards and operational workflows.
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {industries.map((industry, index) => (
            <a
              key={index}
              href={industry.link}
              className="group bg-gray-900/90 border border-gray-800 hover:border-cyan-500/70 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl hover:shadow-cyan-500/15 flex flex-col justify-between"
            >
              {/* Digital Product UI Image Box */}
              <div className="relative h-48 w-full overflow-hidden bg-gray-950">
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 filter brightness-90 group-hover:brightness-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60" />

                {/* Digital Product Badge */}
                <div className="absolute top-3 left-3 bg-gray-950/90 backdrop-blur-md border border-cyan-500/30 px-2.5 py-0.5 rounded text-[11px] font-mono font-bold text-cyan-400">
                  {industry.badge}
                </div>

                <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-950/80 backdrop-blur-md border border-gray-700/80 flex items-center justify-center text-white group-hover:text-cyan-400 group-hover:border-cyan-500 transition-all shadow-md">
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>

              {/* Bottom Content Box - Centered & Slightly Lighter BG */}
              <div className="p-6 pt-0 text-center flex flex-col items-center justify-center bg-gray-900/90 relative border-t border-gray-800/80">
                {/* Floating Centered Icon */}
                <div className={`w-12 h-12 bg-gradient-to-br ${industry.color} rounded-2xl flex items-center justify-center shadow-lg -mt-6 mb-3 border-2 border-gray-900 group-hover:scale-110 transition-transform duration-300`}>
                  <industry.icon className="w-6 h-6 text-white" />
                </div>

                <h3 className="text-lg font-extrabold text-white mb-1 group-hover:text-cyan-300 transition-colors text-center">
                  {industry.name}
                </h3>
                <p className="text-sm text-gray-400 font-medium leading-normal text-center">
                  {industry.subtitle}
                </p>
              </div>
            </a>
          ))}
        </div>

      </div>

      {/* Background Glow */}
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

export default Industries;